export type MessageStatus = 'SENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

/** Numeric message type constants — used in transport and storage */
export const MessageType = {
  TEXT: 20000,
  IMAGE: 20001,
  FILE: 20002,
  VOICE: 20003,
  VIDEO: 20004,
  LOCATION: 20005,
  NOTICE: 20006,
  SYSTEM: 20007,
  CARD: 20008,
} as const;

export type MessageType = typeof MessageType[keyof typeof MessageType] | number;

/** Reverse lookup: string name → numeric value (for backend compatibility) */
const NAME_TO_VALUE: Record<string, number> = {
  TEXT: MessageType.TEXT,
  IMAGE: MessageType.IMAGE,
  FILE: MessageType.FILE,
  VOICE: MessageType.VOICE,
  VIDEO: MessageType.VIDEO,
  LOCATION: MessageType.LOCATION,
  NOTICE: MessageType.NOTICE,
  SYSTEM: MessageType.SYSTEM,
  CARD: MessageType.CARD,
};

/**
 * Normalize a raw messageType from backend to numeric enum value.
 * Handles: number (20000), string-number ("20000"), string-name ("IMAGE").
 * Returns MessageType.TEXT (20000) as safe fallback for unknown values.
 */
export function normalizeMessageType(raw: unknown): number {
  if (typeof raw === 'number') {
    // Known numeric value or arbitrary number — pass through
    return raw;
  }
  if (typeof raw === 'string') {
    // Try string name first ("IMAGE" → 20001)
    if (raw in NAME_TO_VALUE) {
      return NAME_TO_VALUE[raw];
    }
    // Try stringified number ("20001" → 20001)
    const parsed = Number(raw);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  return MessageType.TEXT;
}

export type UploadStatus = 'UPLOADING' | 'UPLOADED' | 'UPLOAD_FAILED';

export type ConversationType = 'friend' | 'group';

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  receiverId: string;
  receiverNickName?: string;
  content: string;
  messageType: MessageType;
  timestamp: number;
  status: MessageStatus;
  isSelf: boolean;
  imageUrl?: string;
  thumbnailBase64?: string;
  thumbnailUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  mimeType?: string;
  uploadStatus?: UploadStatus;
  uploadProgress?: number;
  localPreviewUrl?: string;
  pendingFile?: File;
}

export interface Conversation {
  id: string;
  type: ConversationType;
  name: string;
  avatar?: string;
  groupId?: number;
  lastMessage?: ChatMessage;
  lastMessageAt?: number;
  unreadCount: number;
  isOnline?: boolean;
  friendId?: string;
  groupName?: string;
  groupAvatarPath?: string;
  currentMembers?: number;
  members?: any[];
  lastMessageContent?: string;
}

export interface GetChatHistoryRequest {
  conversationId?: string;
  friendId?: string;
  page: number;
  size: number;
}

export interface GetChatHistoryResponse {
  success: boolean;
  messages: ChatMessage[];
  total: number;
  page: number;
  size: number;
  error?: string;
}

export interface MessageCursor {
  conversationId: string;
  totalMessages: number;
  firstMessageId: string | null;
  lastMessageId: string | null;
  firstMessageTimestamp: number;
  lastMessageTimestamp: number;
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  loadedMessageCount: number;
}

export interface UnreadSyncVersion {
  conversationId: string;
  version: number;
  count: number;
}

import { SocialEvent } from './websocket';

export interface MessageStoreState {
  messages: Map<string, ChatMessage[]>;
  conversations: Map<string, Conversation>;
  unreadCount: Map<string, number>;
  pendingRequestCount: number;
  socialEvents: SocialEvent[];
  currentConversation: string | null;
  totalUnreadCount: number;
  watermarks: Map<string, number>;
  messageCursors: Map<string, MessageCursor>;
  messageIdSet: Map<string, Set<string>>;
  unreadVersions: Map<string, number>;
  
  addMessage: (conversationId: string, message: ChatMessage) => void;
  updateMessageStatus: (conversationId: string, messageId: string, status: MessageStatus) => void;
  updateMessage: (conversationId: string, messageId: string, updates: Partial<ChatMessage>) => void;
  getMessages: (conversationId: string) => ChatMessage[];
  setMessages: (conversationId: string, messages: ChatMessage[]) => void;
  batchSetConversations: (conversations: Map<string, Conversation>) => void;
  batchSetMessages: (messagesMap: Map<string, ChatMessage[]>) => void;
  addConversation: (conversation: Conversation) => void;
  updateConversation: (conversationId: string, updates: Partial<Conversation>) => void;
  getConversation: (conversationId: string) => Conversation | undefined;
  setCurrentConversation: (conversationId: string | null) => void;
  getCurrentConversation: () => string | null;
  markAsRead: (conversationId: string) => void;
  incrementUnreadCount: (conversationId: string) => void;
  getUnreadCount: (conversationId: string) => number;
  syncUnreadCount: (conversationId: string, count: number) => void;
  syncAllUnreadCounts: (counts: Record<string, number>) => void;
  clearMessages: (conversationId: string) => void;
  setPendingRequestCount: (count: number) => void;
  incrementPendingRequestCount: () => void;
  decrementPendingRequestCount: () => void;
  getPendingRequestCount: () => number;
  addSocialEvent: (event: SocialEvent) => void;
  clearSocialEvents: () => void;
  getTotalUnreadCount: () => number;
  resolveConversationsWithGroupInfo: (groups: any[]) => void;
  setWatermark: (conversationId: string, timestamp: number) => void;
  getWatermark: (conversationId: string) => number;
  addMessageId: (conversationId: string, messageId: string) => void;
  hasMessageId: (conversationId: string, messageId: string) => boolean;
  setMessageCursor: (conversationId: string, cursor: MessageCursor) => void;
  getMessageCursor: (conversationId: string) => MessageCursor | undefined;
  syncUnreadCountWithVersion: (conversationId: string, count: number, version: number) => void;
}

/**
 * 生成私聊会话 ID，使用数字排序确保跨语言一致。
 *
 * 排序规则：将 userId 解析为 number 后比较，较小的在前。
 * 这与 Rust generate_private(i64, i64) 和 C++/Dart/Java 版本一致。
 */
export function getConversationId(senderId: string, receiverId: string): string {
  if (!senderId || !receiverId) {
    return '';
  }
  const id1 = parseInt(senderId, 10);
  const id2 = parseInt(receiverId, 10);
  const smaller = Math.min(id1, id2);
  const larger = Math.max(id1, id2);
  return 'conv_private_' + smaller + '_' + larger;
}

export function getGroupConversationId(numericGroupId: string): string {
  if (!numericGroupId) {
    return '';
  }
  return 'conv_group_' + numericGroupId;
}

export function isGroupConversation(conversationId: string): boolean {
  return conversationId.startsWith('conv_group_');
}

export function isPrivateConversation(conversationId: string): boolean {
  return conversationId.startsWith('conv_private_');
}

export function extractOpponentId(conversationId: string, currentUserId: string): string | null {
  if (!conversationId.startsWith('conv_private_')) return null;
  const idsPart = conversationId.substring('conv_private_'.length);
  const underscorePos = idsPart.indexOf('_');
  if (underscorePos < 0) return null;
  const id1 = idsPart.substring(0, underscorePos);
  const id2 = idsPart.substring(underscorePos + 1);
  if (id1 === currentUserId) return id2;
  if (id2 === currentUserId) return id1;
  return null;
}

export function generateTempId(): string {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
